import { useRef, useState } from 'react';
import { getSelectionSplit } from '../../utils/caretUtils';
import { snapshotRect } from '../../utils/floatingMenuPosition';
import { blurActiveEditable } from '../../hooks/useEditorSelection/utils/selectionDom';

// Click vs drag: same 5px threshold as dnd-kit's PointerSensor. Movement
// past that (or an active drag) is a move; a still pointer-up is a click
// and opens the handle menu (Notion-style). Right-click also opens it.
const CLICK_SLOP_PX = 5;

export const useBlockHandleMenu = ({
  block,
  multiSelectedIds,
  onSelect,
  onWrapInCallout,
  activeId,
  isDragging,
}) => {
  const dragStartPosRef = useRef(null);
  // Text selection at pointer-down — a handle mousedown can clear the native
  // range before mouseup opens the menu.
  const pendingSelectionSplitRef = useRef(null);
  // Snapshot wrap targets when the menu opens — a left-click on the menu
  // item would otherwise clear multiSelected before Convert runs.
  const [handleMenu, setHandleMenu] = useState(null); // { selectionSplit, wrapIds } | null

  const openHandleMenu = (handleEl, { selectionSplit: splitOverride } = {}) => {
    const anchorRect = snapshotRect(handleEl.getBoundingClientRect());
    const inMulti =
      multiSelectedIds?.includes(block.id) && multiSelectedIds.length > 1;
    if (block.type === 'callout' || block.type === 'choice') {
      document.dispatchEvent(
        new CustomEvent('doc-callout-menu', {
          detail: {
            blockId: block.id,
            anchorRect,
            // Snapshot so Convert can wrap the full multi-select even after
            // the menu click would otherwise clear multiSelected.
            wrapIds: inMulti ? [...multiSelectedIds] : null,
          },
        })
      );
      return;
    }
    if (!onWrapInCallout) return;
    // Toggle closed if this block's menu is already open
    if (handleMenu) {
      setHandleMenu(null);
      return;
    }
    const editable = handleEl
      .closest('[data-block-id]')
      ?.querySelector('.doc-editable');
    const selectionSplit = inMulti
      ? null
      : splitOverride !== undefined
        ? splitOverride
        : editable
          ? getSelectionSplit(editable)
          : null;
    const wrapIds = inMulti ? [...multiSelectedIds] : [block.id];
    setHandleMenu({ selectionSplit, wrapIds });
  };

  const handleHandleMouseDown = (e) => {
    if (e.button !== 0) return;
    dragStartPosRef.current = { x: e.clientX, y: e.clientY };
    // Capture text highlight before the handle steals focus / clears it
    const editable = e.currentTarget
      .closest('[data-block-id]')
      ?.querySelector('.doc-editable');
    pendingSelectionSplitRef.current = editable
      ? getSelectionSplit(editable)
      : null;
  };

  const handleHandleMouseUp = (e) => {
    if (e.button !== 0) {
      dragStartPosRef.current = null;
      return;
    }
    const start = dragStartPosRef.current;
    dragStartPosRef.current = null;
    if (!start) return;
    // Drag already took over — never open the menu
    if (activeId || isDragging) {
      pendingSelectionSplitRef.current = null;
      return;
    }
    const dx = Math.abs(e.clientX - start.x);
    const dy = Math.abs(e.clientY - start.y);
    if (dx >= CLICK_SLOP_PX || dy >= CLICK_SLOP_PX) {
      pendingSelectionSplitRef.current = null;
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    blurActiveEditable();
    // Select the block (keep multi-select if this block is already in it)
    if (!multiSelectedIds?.includes(block.id)) {
      onSelect?.(block.id);
    }
    const split = pendingSelectionSplitRef.current;
    pendingSelectionSplitRef.current = null;
    openHandleMenu(e.currentTarget, { selectionSplit: split });
  };

  const handleHandleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    pendingSelectionSplitRef.current = null;
    openHandleMenu(e.currentTarget);
  };

  const runConvertToCallout = () => {
    if (!onWrapInCallout || !handleMenu) return;
    onWrapInCallout(block.id, {
      ids: handleMenu.wrapIds,
      selectionSplit: handleMenu.selectionSplit || null,
    });
    setHandleMenu(null);
  };

  return {
    handleMenu,
    closeHandleMenu: () => setHandleMenu(null),
    handleHandleMouseDown,
    handleHandleMouseUp,
    handleHandleContextMenu,
    runConvertToCallout,
  };
};
